/**
 * E-post innboks - Manuell behandling av innkommende e-poster
 *
 * Viser:
 * - Ubehandlede e-poster (venter på advokat)
 * - AI-forslag til klassifisering (kan godkjennes/avvises)
 * - Manuell kobling til sak
 * - Mulighet for å arkivere uten sak
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import './EpostInnboks.css';
import { htmlToText, renderFormattedEmail, getEmailPreview } from '../utils/emailFormatter';
import EpostOppfolgingsforslag from './EpostOppfolgingsforslag';

export default function EpostInnboks() {
  const [ubehandledeEposter, setUbehandledeEposter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [valgtEpost, setValgtEpost] = useState(null);
  const [saker, setSaker] = useState([]);
  const [behandlingsDialog, setBehandlingsDialog] = useState(false);
  const [lesEpostDialog, setLesEpostDialog] = useState(false);
  const [synkroniserer, setSynkroniserer] = useState(false);
  const [synkProgress, setSynkProgress] = useState(null);
  const [arkiverIExchange, setArkiverIExchange] = useState(true); // Default: Ja, flytt til sak-mappen
  const [utvidedEposter, setUtvidedEposter] = useState(new Set()); // Track expanded emails
  const [oppfolgingsForslagDialog, setOppfolgingsForslagDialog] = useState(false); // Oppfølgingsforslag modal
  const [epostForForslag, setEpostForForslag] = useState(null); // E-post som skal analyseres

  useEffect(() => {
    lastUbehandledeEposter();
    lastSaker();
  }, []);

  const lastUbehandledeEposter = async () => {
    try {
      setLoading(true);
      const data = await crmApi.request('/api/epost/ubehandlede');
      setUbehandledeEposter(data.eposter || []);
    } catch (error) {
      console.error('Feil ved lasting av ubehandlede e-poster:', error);
    } finally {
      setLoading(false);
    }
  };

  const lastSaker = async () => {
    try {
      const data = await crmApi.request('/api/saker');
      setSaker(Array.isArray(data) ? data : (data.saker || []));
    } catch (error) {
      console.error('Feil ved lasting av saker:', error);
    }
  };

  const godkjennAIForslag = async (epost) => {
    try {
      const response = await crmApi.request(`/api/epost/${epost.id}/godkjenn-ai`, {
        method: 'POST',
        body: JSON.stringify({
          arkiver_i_exchange: arkiverIExchange
        })
      });

      let melding = `✅ E-post godkjent og knyttet til sak #${epost.ai_foreslått_sak}`;

      if (response.exchange_status === 'flyttet_til_sak') {
        melding += '\n📁 E-post flyttet til "sak" i Exchange';
      } else if (response.exchange_status === 'feilet') {
        melding += '\n⚠️ Kunne ikke flytte e-post i Exchange (allerede behandlet i CRM)';
      }

      alert(melding);
      lastUbehandledeEposter();
    } catch (error) {
      console.error('Feil ved godkjenning:', error);
      alert('Kunne ikke godkjenne AI-forslag');
    }
  };

  const avvisAIForslag = async (epost) => {
    setValgtEpost(epost);
    setBehandlingsDialog(true);
  };

  const manuellKobling = async (sakId) => {
    if (!valgtEpost) return;

    try {
      const response = await crmApi.request(`/api/epost/${valgtEpost.id}/koble-til-sak`, {
        method: 'POST',
        body: JSON.stringify({
          sak_id: sakId,
          arkiver_i_exchange: arkiverIExchange
        })
      });

      let melding = `✅ E-post manuelt koblet til sak #${sakId}`;

      if (response.exchange_status === 'flyttet_til_sak') {
        melding += '\n📁 E-post flyttet til "sak" i Exchange';
      } else if (response.exchange_status === 'feilet') {
        melding += '\n⚠️ Kunne ikke flytte e-post i Exchange (allerede behandlet i CRM)';
      }

      alert(melding);
      setBehandlingsDialog(false);
      setValgtEpost(null);
      lastUbehandledeEposter();
    } catch (error) {
      console.error('Feil ved manuell kobling:', error);
      alert('Kunne ikke koble e-post til sak');
    }
  };

  const arkiverUtenSak = async (epost = valgtEpost) => {
    if (!epost) return;

    if (!window.confirm('Arkivere e-post uten å knytte til sak?')) return;

    try {
      await crmApi.request(`/api/epost/${epost.id}/arkiver-uten-sak`, {
        method: 'POST'
      });

      alert('✅ E-post arkivert uten sak');
      setBehandlingsDialog(false);
      setValgtEpost(null);
      lastUbehandledeEposter();
    } catch (error) {
      console.error('Feil ved arkivering:', error);
      alert('Kunne ikke arkivere e-post');
    }
  };

  const fjernFraCRM = async () => {
    if (!valgtEpost) return;

    if (!window.confirm('Fjerne e-post fra AdvokatCRM?\n\nE-posten vil bli flyttet til "innboks2" i Exchange og fjernet fra systemet.')) return;

    try {
      const response = await crmApi.request(`/api/epost/${valgtEpost.id}/fjern-fra-crm`, {
        method: 'DELETE'
      });

      let melding = '✅ E-post fjernet fra AdvokatCRM';
      if (response.exchange_status === 'flyttet') {
        melding += '\n📁 E-post flyttet til "innboks2" i Exchange';
      } else if (response.exchange_status === 'feilet') {
        melding += '\n⚠️ E-post fjernet fra CRM, men kunne ikke flyttes i Exchange';
      }

      alert(melding);
      setBehandlingsDialog(false);
      setValgtEpost(null);
      lastUbehandledeEposter();
    } catch (error) {
      console.error('Feil ved fjerning fra CRM:', error);
      alert('Kunne ikke fjerne e-post fra CRM');
    }
  };

  const synkroniserEposter = async () => {
    if (!window.confirm('Synkroniser alle uleste e-poster fra Exchange? Dette kan ta litt tid.')) {
      return;
    }

    try {
      setSynkroniserer(true);
      setSynkProgress('Henter uleste e-poster fra Exchange...');

      const response = await crmApi.request('/api/epost/synkroniser', {
        method: 'POST'
      });

      setSynkProgress(`✅ Fullført! ${response.resultater.vellykket} e-poster synkronisert`);

      // Vis detaljer hvis noen feilet
      if (response.resultater.feilet > 0) {
        const feiledeMeldinger = response.resultater.detaljer
          .filter(d => d.status === 'feilet')
          .map(d => `${d.subject}: ${d.error}`)
          .join('\n');

        alert(`⚠️ ${response.resultater.feilet} e-poster feilet:\n\n${feiledeMeldinger}`);
      }

      // Oppdater liste
      setTimeout(() => {
        lastUbehandledeEposter();
        setSynkroniserer(false);
        setSynkProgress(null);
      }, 2000);

    } catch (error) {
      console.error('Feil ved synkronisering:', error);
      alert('Kunne ikke synkronisere e-poster: ' + error.message);
      setSynkroniserer(false);
      setSynkProgress(null);
    }
  };

  const testExchangeTilkobling = async () => {
    try {
      setSynkroniserer(true);
      setSynkProgress('Tester Exchange-tilkobling...');

      console.log('🧪 Testing Exchange connection...');
      console.log('API URL:', crmApi.baseURL);
      console.log('Endpoint:', '/api/epost/test-exchange');

      const response = await crmApi.request('/api/epost/test-exchange', {
        method: 'GET'
      });

      console.log('✅ Response received:', response);

      setSynkroniserer(false);
      setSynkProgress(null);

      // Vis detaljert testresultat
      const resultat = response.tests.map(test =>
        `${test.status ? '✅' : '❌'} ${test.test}: ${test.status ? 'OK' : test.error || 'Feilet'}`
      ).join('\n');

      alert(`📊 Exchange Test Resultat:\n\n${resultat}\n\n${response.message}`);

      // Hvis noen tester feilet, vis flere detaljer
      if (!response.success) {
        console.error('Exchange test detaljer:', response);
      }

    } catch (error) {
      console.error('❌ Fullstendig feil:', error);
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Stack:', error.stack);

      // Vis mer detaljert feilmelding
      let feilmelding = 'Kunne ikke teste Exchange-tilkobling:\n\n';
      feilmelding += `Type: ${error.constructor.name}\n`;
      feilmelding += `Melding: ${error.message}\n\n`;
      feilmelding += 'Sjekk browser console (F12) for mer info.';

      alert(feilmelding);
      setSynkroniserer(false);
      setSynkProgress(null);
    }
  };

  const getKonfidenseFarge = (confidence) => {
    if (confidence >= 0.9) return '#22c55e'; // Grønn (høy)
    if (confidence >= 0.7) return '#eab308'; // Gul (middels)
    return '#ef4444'; // Rød (lav)
  };

  const getPrioritetIkon = (epost) => {
    const subject = epost.subject?.toLowerCase() || '';
    const body = epost.body?.toLowerCase() || '';

    if (subject.includes('frist') || body.includes('frist')) return '🔴';
    if (subject.includes('?') || body.includes('?')) return '🟡';
    if (epost.has_attachments) return '📎';
    return '📧';
  };

  const formaterDato = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const timer = Math.floor(diff / (1000 * 60 * 60));

    if (timer < 1) return 'Akkurat nå';
    if (timer < 24) return `${timer}t siden`;
    return date.toLocaleDateString('nb-NO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const toggleUtvidEpost = (epostId) => {
    const nySet = new Set(utvidedEposter);
    if (nySet.has(epostId)) {
      nySet.delete(epostId);
    } else {
      nySet.add(epostId);
    }
    setUtvidedEposter(nySet);
  };

  if (loading) {
    return (
      <div className="epost-innboks">
        <div className="epost-header">
          <h3>📬 E-post innboks</h3>
        </div>
        <div className="epost-loading">Laster ubehandlede e-poster...</div>
      </div>
    );
  }

  return (
    <div className="epost-innboks">
      <div className="epost-header">
        <div>
          <h3>📬 E-post innboks</h3>
          <p className="epost-count">
            {ubehandledeEposter.length} ubehandlet{ubehandledeEposter.length !== 1 ? 'e' : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn-synkroniser"
            onClick={synkroniserEposter}
            disabled={synkroniserer}
          >
            {synkroniserer ? '⏳ Synkroniserer...' : '📥 Hent fra Exchange'}
          </button>
          <button
            className="btn-test"
            onClick={testExchangeTilkobling}
            disabled={synkroniserer}
            title="Test Exchange-tilkobling og diagnostikk"
          >
            🔧 Test
          </button>
          <button className="btn-refresh" onClick={lastUbehandledeEposter}>
            🔄 Oppdater
          </button>
        </div>
      </div>

      {synkProgress && (
        <div style={{
          padding: '15px',
          background: '#e0f2fe',
          border: '2px solid #0284c7',
          borderRadius: '8px',
          marginBottom: '15px',
          textAlign: 'center',
          fontWeight: 'bold',
          color: '#0c4a6e'
        }}>
          {synkProgress}
        </div>
      )}

      {/* Exchange arkivering-innstilling */}
      <div style={{
        padding: '12px',
        background: '#f0f9ff',
        border: '1px solid #bae6fd',
        borderRadius: '6px',
        marginBottom: '15px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <input
          type="checkbox"
          id="arkiver-exchange"
          checked={arkiverIExchange}
          onChange={(e) => setArkiverIExchange(e.target.checked)}
          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
        />
        <label htmlFor="arkiver-exchange" style={{ cursor: 'pointer', fontSize: '14px', color: '#0c4a6e', fontWeight: '500' }}>
          📁 Flytt til "sak"-mappen i Exchange etter behandling
        </label>
      </div>

      {ubehandledeEposter.length === 0 ? (
        <div className="epost-empty">
          <p>✅ Ingen ubehandlede e-poster</p>
          <small>Alle e-poster er klassifisert og arkivert</small>
        </div>
      ) : (
        <div className="epost-liste">
          {ubehandledeEposter.map((epost) => {
            const erUtvidet = utvidedEposter.has(epost.id);
            return (
            <div key={epost.id} className="epost-item">
              <div className="epost-item-header">
                <span className="epost-prioritet">{getPrioritetIkon(epost)}</span>
                <div className="epost-metadata">
                  <div>
                    <strong>Fra:</strong> {epost.from_address}
                  </div>
                  {epost.to_addresses && epost.to_addresses.length > 0 && (
                    <div className="epost-to">
                      <strong>Til:</strong> {epost.to_addresses.join(', ')}
                    </div>
                  )}
                  <span className="epost-dato">{formaterDato(epost.received_at)}</span>
                </div>
              </div>

              <div className="epost-subject">
                <strong>Emne:</strong> {epost.subject || '(Ingen emne)'}
              </div>

              <div className={erUtvidet ? "epost-preview epost-preview-utvidet" : "epost-preview"}>
                {erUtvidet
                  ? renderFormattedEmail(epost.body)
                  : getEmailPreview(epost.body, 500)
                }
              </div>

              <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                <button
                  className="btn-utvid-epost"
                  onClick={() => toggleUtvidEpost(epost.id)}
                >
                  {erUtvidet ? '⬆️ Vis mindre' : '⬇️ Vis mer'}
                </button>
                <button
                  className="btn-oppfolgingsforslag"
                  onClick={() => {
                    setEpostForForslag(epost);
                    setOppfolgingsForslagDialog(true);
                  }}
                  title="AI-analyse: Se forslag til oppfølginger basert på e-postinnhold"
                >
                  🤖 Oppfølging
                </button>
              </div>

              {/* AI-forslag */}
              {epost.ai_classified && epost.ai_foreslått_sak ? (
                <div className="epost-ai-forslag">
                  <div className="ai-forslag-header">
                    <span className="ai-ikon">🤖</span>
                    <div className="ai-forslag-info">
                      <strong>AI foreslår:</strong> Sak #{epost.ai_foreslått_sak} - {epost.ai_foreslått_sakstittel}
                      <div
                        className="ai-confidence"
                        style={{ color: getKonfidenseFarge(epost.classification_confidence) }}
                      >
                        Sikkerhet: {Math.round(epost.classification_confidence * 100)}%
                      </div>
                    </div>
                  </div>

                  <div className="ai-forslag-actions">
                    {epost.classification_confidence >= 0.7 ? (
                      <>
                        <button
                          className="btn-godkjenn"
                          onClick={() => godkjennAIForslag(epost)}
                        >
                          ✅ Godkjenn
                        </button>
                        <button
                          className="btn-avvis"
                          onClick={() => avvisAIForslag(epost)}
                        >
                          ❌ Velg annen sak
                        </button>
                        <button
                          className="btn-arkiver-uten-sak"
                          onClick={() => arkiverUtenSak(epost)}
                        >
                          🗃️ Arkiver uten sak
                        </button>
                      </>
                    ) : (
                      <div className="ai-lav-confidence">
                        ⚠️ Lav sikkerhet - vennligst velg sak manuelt
                        <button
                          className="btn-manuell"
                          onClick={() => avvisAIForslag(epost)}
                        >
                          📝 Velg sak
                        </button>
                        <button
                          className="btn-arkiver-uten-sak"
                          onClick={() => arkiverUtenSak(epost)}
                        >
                          🗃️ Arkiver uten sak
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="epost-ingen-forslag">
                  <p>🔍 AI fant ingen match</p>
                  <button
                    className="btn-manuell"
                    onClick={() => {
                      setValgtEpost(epost);
                      setBehandlingsDialog(true);
                    }}
                  >
                    📝 Velg sak manuelt
                  </button>
                  <button
                    className="btn-arkiver-uten-sak"
                    onClick={() => arkiverUtenSak(epost)}
                  >
                    🗃️ Arkiver uten sak
                  </button>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Manuell behandlings-dialog */}
      {behandlingsDialog && valgtEpost && (
        <div className="modal-overlay" onClick={() => setBehandlingsDialog(false)}>
          <div className="modal-content behandlings-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>📝 Koble e-post til sak</h2>
              <button className="btn-close" onClick={() => setBehandlingsDialog(false)}>✕</button>
            </div>

            <div className="epost-info">
              <strong>Fra:</strong> {valgtEpost.from_address}<br />
              <strong>Emne:</strong> {valgtEpost.subject}<br />
              <strong>Mottatt:</strong> {new Date(valgtEpost.received_at).toLocaleString('nb-NO')}
            </div>

            <div className="epost-innhold-seksjon">
              <h3>📧 E-post innhold:</h3>
              <div className="epost-fulltext">
                {renderFormattedEmail(valgtEpost.body)}
              </div>
            </div>

            <div className="sak-velger">
              <h3>Velg sak:</h3>
              <div className="sak-liste">
                {saker.map((sak) => (
                  <div
                    key={sak.id}
                    className="sak-item"
                    onClick={() => manuellKobling(sak.id)}
                  >
                    <div className="sak-nummer">#{sak.saksnummer}</div>
                    <div className="sak-info">
                      <strong>{sak.sakstittel}</strong>
                      <small>{sak.klient_navn}</small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="dialog-footer">
              <button className="btn-arkiver-uten-sak" onClick={arkiverUtenSak}>
                🗃️ Arkiver uten sak
              </button>
              <button className="btn-fjern-crm" onClick={fjernFraCRM}>
                🗑️ Fjern fra CRM
              </button>
              <button className="btn-avbryt" onClick={() => setBehandlingsDialog(false)}>
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Les e-post dialog */}
      {lesEpostDialog && valgtEpost && (
        <div className="modal-overlay" onClick={() => setLesEpostDialog(false)}>
          <div className="modal-content les-epost-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>📧 E-post</h2>
              <button className="btn-close" onClick={() => setLesEpostDialog(false)}>✕</button>
            </div>

            <div className="epost-info">
              <strong>Fra:</strong> {valgtEpost.from_address}<br />
              <strong>Emne:</strong> {valgtEpost.subject}<br />
              <strong>Mottatt:</strong> {new Date(valgtEpost.received_at).toLocaleString('nb-NO')}
            </div>

            <div className="epost-fulltext-container">
              <div className="epost-fulltext">
                {renderFormattedEmail(valgtEpost.body)}
              </div>
            </div>

            <div className="dialog-footer">
              <button
                className="btn-manuell"
                onClick={() => {
                  setLesEpostDialog(false);
                  setBehandlingsDialog(true);
                }}
              >
                📝 Koble til sak
              </button>
              <button className="btn-fjern-crm" onClick={fjernFraCRM}>
                🗑️ Fjern fra CRM
              </button>
              <button className="btn-avbryt" onClick={() => setLesEpostDialog(false)}>
                Lukk
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Oppfølgingsforslag dialog */}
      {oppfolgingsForslagDialog && epostForForslag && (
        <EpostOppfolgingsforslag
          epost={epostForForslag}
          onLukk={() => {
            setOppfolgingsForslagDialog(false);
            setEpostForForslag(null);
          }}
          onOppgaverOpprettet={() => {
            // Callback når oppgaver er opprettet - kan oppdatere liste hvis nødvendig
            console.log('✅ Oppgaver opprettet fra e-post');
          }}
        />
      )}
    </div>
  );
}
